import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BookSuggestion } from '@/types/chat';
import { toast } from 'sonner';

export const useBookClubSuggestions = (chatId: string | null) => {
  const [suggestions, setSuggestions] = useState<BookSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSuggestions = async () => {
    if (!chatId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_book_suggestions')
        .select(`
          *,
          book:books(id, title, authors, cover_url)
        `)
        .eq('chat_id', chatId)
        .order('suggested_at', { ascending: false });

      if (error) throw error;
      
      // Fetch suggesters separately
      const suggesterIds = data?.map(s => s.suggested_by) || [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, name')
        .in('id', suggesterIds);
      
      const formattedData: BookSuggestion[] = (data || []).map((suggestion: any) => ({
        ...suggestion,
        suggester: profiles?.find(p => p.id === suggestion.suggested_by)
      }));
      
      setSuggestions(formattedData);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast.error('Failed to load book suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [chatId]);

  // Real-time subscription
  useEffect(() => {
    if (!chatId) return;

    const channel = supabase
      .channel(`book-suggestions-${chatId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_book_suggestions',
          filter: `chat_id=eq.${chatId}`
        },
        () => {
          fetchSuggestions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  const addSuggestion = async (bookId: string, notes?: string) => {
    if (!chatId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('chat_book_suggestions')
        .insert({
          chat_id: chatId,
          book_id: bookId,
          suggested_by: user.id,
          notes,
          status: 'suggested'
        });

      if (error) throw error;
      toast.success('📚 Book added to reading list!');
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error('This book is already in the reading list');
      } else {
        console.error('Error adding suggestion:', error);
        toast.error('Failed to add book suggestion');
      }
    }
  };

  const updateSuggestionStatus = async (suggestionId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('chat_book_suggestions')
        .update({ status })
        .eq('id', suggestionId);

      if (error) throw error;
      toast.success(`Book marked as ${status}`);
    } catch (error) {
      console.error('Error updating suggestion:', error);
      toast.error('Failed to update book status');
    }
  };

  const voteForSuggestion = async (suggestionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('chat_book_votes')
        .insert({
          suggestion_id: suggestionId,
          user_id: user.id
        });

      if (error) throw error;

      // Fetch current vote count and increment
      const { data: currentData } = await supabase
        .from('chat_book_suggestions')
        .select('vote_count')
        .eq('id', suggestionId)
        .single();
      
      const { error: updateError } = await supabase
        .from('chat_book_suggestions')
        .update({ vote_count: (currentData?.vote_count || 0) + 1 })
        .eq('id', suggestionId);

      if (updateError) throw updateError;
      
      fetchSuggestions();
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error('You already voted for this book');
      } else {
        console.error('Error voting:', error);
        toast.error('Failed to vote');
      }
    }
  };

  const removeVote = async (suggestionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('chat_book_votes')
        .delete()
        .eq('suggestion_id', suggestionId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Fetch current vote count and decrement
      const { data: currentData } = await supabase
        .from('chat_book_suggestions')
        .select('vote_count')
        .eq('id', suggestionId)
        .single();
      
      const { error: updateError } = await supabase
        .from('chat_book_suggestions')
        .update({ vote_count: Math.max((currentData?.vote_count || 0) - 1, 0) })
        .eq('id', suggestionId);

      if (updateError) throw updateError;
      
      fetchSuggestions();
    } catch (error) {
      console.error('Error removing vote:', error);
      toast.error('Failed to remove vote');
    }
  };

  return {
    suggestions,
    loading,
    addSuggestion,
    updateSuggestionStatus,
    voteForSuggestion,
    removeVote,
    refetch: fetchSuggestions
  };
};
